import { useEffect, useState } from "react";
import { PageHead } from "@/components/PageHead";

import { Field } from "@/components/Field";
import { Select } from "@/components/inputs";
import { EnrollAssetForm } from "@/modules/registry/AssetsInventory";
import { CsvUpload } from "@/components/CsvUpload";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { registry, type AssetRowIn, type EnrolledPart, type PartRowIn } from "@/api/client";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { CompanySelect, EnrollPartForm, SUBCOMPONENT_KINDS, parseAssetsCsvFor, parsePartsCsvFor, useCompanyScope } from "@/modules/registry/parts_common";
import { KindSelect, useTemplates } from "@/modules/asset_catalog/common";

type What = "part" | "asset";

export function RegistryEnrollPage() {
  const qc = useQueryClient();
  const [what, setWhat] = useState<What>("part");

  // WHO WILL OWN IT. Asked once for the page: all four enrol doors below use
  // this one answer, and none of them may fall back to the session's company.
  const { companies, scoped } = useCompanyScope();
  const [owner, setOwner] = useState("");
  // Preselect ONLY when the control has collapsed to plain text — which is
  // what CompanySelect does for a scoped caller with one company. There is
  // nothing to click in that state, so requiring a click would make enrolling
  // impossible for them.
  //
  // When a DROPDOWN is shown, the choice stays deliberate. An operator picking
  // who owns new stock should say so rather than inherit whoever happens to be
  // first in the list; the placeholder row is the default, and the actions
  // below stay disabled until it is answered.
  //
  // (A previous version preselected whenever there was one company, to work
  // around a blank page. The real cause of that was a MISSING placeholder
  // option — value "" matched nothing, so the browser displayed the first
  // company while state stayed empty. The placeholder fixed it; this no longer
  // has to.)
  const collapsed = scoped && companies.length === 1;
  useEffect(() => {
    if (collapsed && !owner) setOwner(companies[0].company_id);
  }, [collapsed, companies, owner]);
  const [partsDone, setPartsDone] = useState("");
  const tpl = useTemplates().data;
  const [bulkKind, setBulkKind] = useState("");
  const [aBulkKind, setABulkKind] = useState<"vehicle" | "battery">("vehicle");
  const [bulkRows, setBulkRows] = useState<PartRowIn[] | null>(null);
  const [bulkDone, setBulkDone] = useState<EnrolledPart[] | null>(null);
  const bulkFb = useMutationFeedback();
  const [aBulkRows, setABulkRows] = useState<AssetRowIn[] | null>(null);
  const [aBulkDone, setABulkDone] = useState("");
  const aBulkFb = useMutationFeedback();
  const aBulk = useMutation({
    mutationFn: (rows: AssetRowIn[]) => registry.enrollAssets(rows, owner),
    onSuccess: (r) => { aBulkFb.ok(); setABulkRows(null); setABulkDone(`${r.enrolled.length} assets enrolled`); void qc.invalidateQueries({ queryKey: ["registry"] }); },
    onError: aBulkFb.fail,
  });
  const bulkEnroll = useMutation({
    mutationFn: (rows: PartRowIn[]) => registry.enrollParts(rows, owner),
    onSuccess: (r) => { bulkFb.ok(); setBulkDone(r.enrolled); setBulkRows(null); void qc.invalidateQueries({ queryKey: ["registry"] }); },
    onError: bulkFb.fail,
  });
  // ONE element, rendered in each enrol panel. A PLACEHOLDER row rather than
  // `allLabel={null}`: with no option carrying value "", a browser shows the
  // FIRST company while `owner` stays empty — the control would display an
  // answer its state does not hold. It says "choose", never "any"; the action
  // in each panel stays disabled until a real company is picked.
  const ownerField = (
    <CompanySelect value={owner} onChange={setOwner} companies={companies}
                   scoped={scoped} allLabel="— choose a company —"
                   style={{ minWidth: 220 }} />
  );

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry" title="Enroll"
        desc="One door for everything that enters the registry. A telematics unit, connected BMS / VCU / cluster are parts with a mandatory Connection ID (IMEI); every other part has none. Assets are vehicles and batteries (VIN / BIN). Whatever you enrol enters the chosen company's INVENTORY — owner and custodian both, state `inventory`."
        right={
          <Select value={what} onChange={(e) => { setWhat(e.target.value as What); setPartsDone(""); }} style={{ minWidth: 220 }}>
            <option value="part">Part (telematics unit, BMS, motor, cluster ...)</option>
            <option value="asset">Asset (vehicle / battery)</option>
          </Select>
        } />

      {what === "part" && (<>
        <div className="section-label">Enroll a single part</div>
        <div className="panel panel-pad">
          <EnrollPartForm owner={owner} ownerField={ownerField} onDone={setPartsDone} />
          {partsDone && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ok)" }}>{partsDone}</div>}
        </div>
        <div className="section-label">Bulk enroll (CSV) — one kind per sheet</div>
        <div className="panel panel-pad">
          {/* Both settings as labelled fields, in one row, like every other
              form on this page — rather than a loose span for one and a
              dropdown hidden in the action row for the other. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
                        gap: 14, marginBottom: 14 }}>
            <Field label="Owner" req>{ownerField}</Field>
            <Field label="Sheet is for" req>
              <KindSelect kinds={tpl?.kinds ?? []} only={SUBCOMPONENT_KINDS} value={bulkKind}
                onChange={(k) => { setBulkKind(k); setBulkRows(null); setBulkDone(null); }}
                placeholder="— choose kind —" style={{ width: "100%" }} />
            </Field>
            {/* Same line: the template you want depends on the kind you just
                picked, so asking for it anywhere else is a second journey. */}
            <Field label="Template">
              <a className={`btn ghost sm${bulkKind ? "" : " disabled"}`}
                 href={bulkKind ? registry.partsTemplateUrl(bulkKind) : undefined}
                 aria-disabled={!bulkKind}
                 style={{ display: "inline-flex", alignItems: "center",
                          pointerEvents: bulkKind ? undefined : "none",
                          opacity: bulkKind ? 1 : 0.5 }}>⭳ Download .csv</a>
            </Field>
          </div>
          {bulkKind ? (
          <CsvUpload<PartRowIn> key={bulkKind}
            templateUrl={registry.partsTemplateUrl(bulkKind)}
            parse={parsePartsCsvFor(bulkKind)} onParsed={(items) => { setBulkRows(items); setBulkDone(null); }}
            onSubmit={(rows) => bulkEnroll.mutate(rows)}
            actionLabel={(n) => `Enroll ${n} ${tpl?.kinds.find((k) => k.id === bulkKind)?.label ?? "part"}${n === 1 ? "" : "s"}`}
            disabled={!owner}
            busy={bulkEnroll.isPending} error={bulkFb.errorNode} />
          ) : null}
          {bulkRows && bulkRows.length > 0 && !bulkDone && (
            <div style={{ fontSize: 12, color: "var(--t-mute)", marginTop: 6 }}>{bulkRows.length} rows parsed — check the counts, then Enroll.</div>
          )}
          {bulkDone && (
            <div style={{ marginTop: 14, padding: "12px 14px", border: "1px solid color-mix(in srgb, var(--ok) 40%, transparent)", borderRadius: 10, background: "color-mix(in srgb, var(--ok) 6%, transparent)" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "var(--ok)", letterSpacing: ".5px", textTransform: "uppercase" }}>
                {bulkDone.length} enrolled{bulkDone.some((d) => d.credential) ? " — bake these credentials" : ""}
              </div>
              <div className="tblwrap" style={{ marginTop: 8 }}>
                <table className="tbl">
                  <thead><tr><th>Serial</th><th>Connection ID</th><th>BLE MAC</th><th>MQTT password</th></tr></thead>
                  <tbody>
                    {bulkDone.map((d) => (
                      <tr key={d.part_id}>
                        <td className="mono">{d.serial_no}</td>
                        <td className="mono">{d.connection_id ?? "—"}</td>
                        <td className="mono">{d.ble_mac ?? "—"}</td>
                        <td className="mono">{d.credential ? d.credential.secret : (d.connection_id ? "existing — unchanged" : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {bulkDone.some((d) => d.credential) && (
                  <button className="btn ghost" onClick={() => { void navigator.clipboard?.writeText(bulkDone.filter((d) => d.credential).map((d) => `${d.connection_id},${d.credential!.secret}`).join(String.fromCharCode(10))); }}>Copy credentials (CSV)</button>
                )}
                <button className="btn ghost" onClick={() => setBulkDone(null)}>Dismiss</button>
              </div>
            </div>
          )}
        </div>
      </>)}

      {what === "asset" && (<>
        <div className="section-label">Enroll a single asset</div>
        <div className="panel panel-pad">
          <EnrollAssetForm owner={owner} ownerField={ownerField} onDone={setPartsDone} />
          {partsDone && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ok)" }}>{partsDone}</div>}
        </div>
        <div className="section-label">Bulk enroll (CSV) — one kind per sheet</div>
        <div className="panel panel-pad">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
                        gap: 14, marginBottom: 14 }}>
            <Field label="Owner" req>{ownerField}</Field>
            <Field label="Sheet is for" req>
              <Select className="fill" value={aBulkKind}
                onChange={(e) => { setABulkKind(e.target.value as "vehicle" | "battery"); setABulkRows(null); setABulkDone(""); }}>
                <option value="vehicle">Vehicles (VIN + number plate)</option>
                <option value="battery">Batteries (BIN)</option>
              </Select>
            </Field>
            <Field label="Template">
              <a className="btn ghost" href={registry.assetsTemplateUrl(aBulkKind)}
                 style={{ display: "inline-flex", alignItems: "center" }}>⭳ Download .csv</a>
            </Field>
          </div>
          <CsvUpload<AssetRowIn> key={aBulkKind}
            templateUrl={registry.assetsTemplateUrl(aBulkKind)}
            parse={parseAssetsCsvFor(aBulkKind)} onParsed={(items) => { setABulkRows(items); setABulkDone(""); }}
            onSubmit={(rows) => aBulk.mutate(rows)}
            actionLabel={(n) => `Enroll ${n} asset${n === 1 ? "" : "s"}`}
            disabled={!owner}
            busy={aBulk.isPending} error={aBulkFb.errorNode} />
          {aBulkRows && aBulkRows.length > 0 && !aBulkDone && (
            <div style={{ fontSize: 12, color: "var(--t-mute)", marginTop: 6 }}>{aBulkRows.length} rows parsed — check the count, then Enroll.</div>
          )}
          {aBulkDone && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ok)" }}>{aBulkDone}</div>}
        </div>
      </>)}

    </div>
  );
}
